import React, { useState, useMemo, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import debounce from 'lodash/fp/debounce'
import get from 'lodash/fp/get'
import type { DocumentNode } from 'graphql'

import { SearchContainer, SearchInput, SearchIcon, ResultsList } from './style'

interface SearchResult {
  value: string
  displayText: string
  data: Record<string, unknown>
}

interface SearchBarProps {
  query: DocumentNode
  dataPath: string
  variables: Record<string, unknown>
  valueField: string
  displayFields: string[]
  excludeValues?: string[]
  placeholder?: string
  onResultClick?: (result: SearchResult) => void
  className?: string
}

const SearchBar: React.FC<SearchBarProps> = ({
  query,
  dataPath,
  variables,
  valueField,
  displayFields,
  excludeValues = [],
  placeholder = 'Search...',
  onResultClick,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [showResults, setShowResults] = useState(false)

  const [executeSearch, { loading }] = useLazyQuery(query, {
    onCompleted: data => {
      const rawResults = get(dataPath, data) || []
      const formattedResults = rawResults
        .map((item: Record<string, unknown>) => ({
          value: get(valueField, item) as string,
          displayText: displayFields
            .map(field => get(field, item) as string)
            .filter(Boolean)
            .join(' '),
          data: item,
        }))
        .filter((result: SearchResult) => !excludeValues.includes(result.value))
      setResults(formattedResults)
      setShowResults(true)
    },
    onError: () => {
      setResults([])
      setShowResults(false)
    },
  })

  const debouncedSearch = useMemo(
    () =>
      debounce(500, (term: string) => {
        if (term.length >= 3) {
          executeSearch({
            variables: {
              ...variables,
              search: term,
            },
          })
        } else {
          setResults([])
          setShowResults(false)
        }
      }),
    [executeSearch, variables]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      setSearchTerm(value)

      if (value.trim() === '') {
        setResults([])
        setShowResults(false)
      } else {
        debouncedSearch(value)
      }
    },
    [debouncedSearch]
  )

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      if (onResultClick) {
        onResultClick(result)
      }
      setSearchTerm('')
      setResults([])
      setShowResults(false)
    },
    [onResultClick]
  )

  const handleInputBlur = useCallback(() => {
    setShowResults(false)
  }, [])

  const handleInputFocus = useCallback(() => {
    if (results.length > 0) {
      setShowResults(true)
    }
  }, [results.length])

  return (
    <SearchContainer className={className}>
      <SearchInput
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        placeholder={placeholder}
      />
      <SearchIcon size={20} />

      {showResults && results.length > 0 && (
        <ResultsList>
          {results.map(result => (
            <button
              key={result.value}
              className="result-item"
              type="button"
              onMouseDown={e => {
                e.preventDefault() // Previene que el input pierda el foco
                handleResultClick(result)
              }}
            >
              {result.displayText}
            </button>
          ))}
        </ResultsList>
      )}

      {showResults &&
        results.length === 0 &&
        !loading &&
        searchTerm.length >= 3 && (
          <ResultsList>
            <div className="no-results">No results found</div>
          </ResultsList>
        )}

      {loading && searchTerm.length >= 3 && (
        <ResultsList>
          <div className="result-item">Searching...</div>
        </ResultsList>
      )}
    </SearchContainer>
  )
}

export default SearchBar
